import { useEffect, useState } from 'preact/hooks';
import { ApiError, createApi, type Api } from './api.js';
import { loadSession, saveSession } from './auth.js';
import { useLang, type Lang } from './i18n.js';
import { Dialog } from './ui.js';

/**
 * "Visa som" (owner decision 2026-10-18): an admin looks at the Hub as a person, a role or a client contact, read only.
 * The view token lives in this tab only (sessionStorage) next to the admin's own sign-in; the API checks both on every
 * request and refuses every change. Starting reloads the Hub as the other person; stopping goes back to where you were.
 */
export type ViewAsMode = 'person' | 'role' | 'client';
export interface ViewAsState {
  token: string;
  mode: ViewAsMode;
  name: string;
  expiresAt: string;
  /** the page to return to */
  back: string;
}

const KEY = 'kff.viewAs';
const PORTAL_KEY = 'kff.portal.preview';

const read = (key: string, now = Date.now()): ViewAsState | null => {
  try {
    const s = JSON.parse(sessionStorage.getItem(key) ?? 'null') as ViewAsState | null;
    return s && Date.parse(s.expiresAt) > now ? s : null;
  } catch {
    return null;
  }
};
const write = (key: string, s: ViewAsState | null) => {
  try {
    if (s) sessionStorage.setItem(key, JSON.stringify(s));
    else sessionStorage.removeItem(key);
  } catch {
    /* private mode: viewing as someone needs this tab's storage, the Hub stays as yourself */
  }
};

/** The Hub's view token, if this tab is looking as someone. */
export const loadViewAs = (): ViewAsState | null => read(KEY);
/** The portal preview, if this tab is looking at the portal as a contact. */
export const loadPortalPreview = (): ViewAsState | null => read(PORTAL_KEY);
export const clearViewAs = (): void => {
  write(KEY, null);
  write(PORTAL_KEY, null);
};

const TX = {
  en: {
    title: 'View as…',
    sub: 'See the Hub the way someone else sees it. Nothing you do is saved while you look.',
    pick: 'Person or role',
    choose: 'Choose…',
    roles: 'Roles',
    people: 'People',
    roleHint: "A role shows that role's menus and buttons. To see exactly someone's own projects and work, choose the person.",
    start: 'View as',
    cancel: 'Cancel',
    signIn: 'Sign in again',
    bar: (name: string, mode: ViewAsMode) => (mode === 'client' ? `You are viewing the portal as ${name}` : mode === 'role' ? `You are viewing the Hub as the role ${name}` : `You are viewing the Hub as ${name}`),
    readOnly: 'view only, nothing is saved',
    until: (t: string) => `until ${t}`,
    stop: 'Stop viewing as',
    asClient: 'View as client',
    failed: "Couldn't start viewing as someone else."
  },
  sv: {
    title: 'Visa som…',
    sub: 'Se Hubben som någon annan ser den. Inget du gör sparas medan du tittar.',
    pick: 'Person eller roll',
    choose: 'Välj…',
    roles: 'Roller',
    people: 'Personer',
    roleHint: 'En roll visar rollens menyer och knappar. Vill du se exakt någons egna projekt och arbete, välj personen.',
    start: 'Visa som',
    cancel: 'Avbryt',
    signIn: 'Logga in igen',
    bar: (name: string, mode: ViewAsMode) => (mode === 'client' ? `Du tittar på portalen som ${name}` : mode === 'role' ? `Du tittar på Hubben som rollen ${name}` : `Du tittar på Hubben som ${name}`),
    readOnly: 'bara titta, inget sparas',
    until: (t: string) => `till ${t}`,
    stop: 'Sluta visa som',
    asClient: 'Visa som kund',
    failed: 'Det gick inte att visa som någon annan.'
  }
};
export const viewAsText = (lang: Lang) => TX[lang];

let allowed = false;
/** Set by the Hub once it knows the signed-in person: an admin who is not already looking as someone. */
export const allowViewAs = (on: boolean): void => {
  allowed = on;
};
/** Whether "Visa som" may be offered here (for menus far from the signed-in person, such as a project's contacts). */
export const viewAsAllowed = (): boolean => allowed;

/** Starts viewing as a person, role or client contact; the page reloads as them. Throws the API's error (step-up, 403). */
export async function startViewAs(api: Api, body: { personId: string } | { role: string } | { contactId: string }): Promise<void> {
  const r = await api.post<{ token: string; expiresAt: string; mode: ViewAsMode; name: string }>('/api/view-as', body);
  const s: ViewAsState = { ...r, back: location.pathname + location.search };
  clearViewAs();
  write(r.mode === 'client' ? PORTAL_KEY : KEY, s);
  location.assign(r.mode === 'client' ? '/portal' : '/');
}

/** Back as yourself: the log gets the end (sent with your own sign-in only), then the page you came from. */
export async function stopViewAs(): Promise<void> {
  const s = loadViewAs() ?? loadPortalPreview();
  clearViewAs();
  const session = loadSession();
  if (s && session) {
    try {
      await createApi({ kind: 'staff', token: session.accessToken }).post('/api/view-as/end', { mode: s.mode, name: s.name });
    } catch {
      /* the start is logged; a lost end only leaves the log without the stop time */
    }
  }
  location.assign(s?.back ?? '/');
}

const clock = (iso: string, lang: Lang) => new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' }).format(new Date(iso));

/** The strip under the top bar while looking as someone; at the end of the 30 minutes it goes back on its own. */
export function ViewAsBar(p: { state: ViewAsState; lang?: Lang }) {
  const ctx = useLang();
  const lang = p.lang ?? ctx;
  const t = TX[lang];
  useEffect(() => {
    const ms = Date.parse(p.state.expiresAt) - Date.now();
    const id = setTimeout(() => void stopViewAs(), Math.max(0, ms));
    return () => clearTimeout(id);
  }, [p.state.expiresAt]);
  return (
    <div class="viewas-bar" role="status">
      <span class="viewas-text">
        <strong>{t.bar(p.state.name, p.state.mode)}</strong> · {t.readOnly} · {t.until(clock(p.state.expiresAt, lang))}
      </span>
      <button type="button" class="btn btn-sm" onClick={() => void stopViewAs()}>
        {t.stop}
      </button>
    </div>
  );
}

interface Options {
  roles: { key: string; label: string }[];
  people: { id: string; name: string; kind: 'employee' | 'freelancer'; roles: string[] }[];
}

/** Pick a person or a role; a stale Google sign-in asks for a new one first (step-up, as for other admin changes). */
export function ViewAsDialog(p: { api: Api; onClose: () => void; personId?: string }) {
  const t = TX[useLang()];
  const [opts, setOpts] = useState<Options | null>(null);
  const [pick, setPick] = useState(p.personId ? `p:${p.personId}` : '');
  const [err, setErr] = useState<{ text: string; stepUp: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    p.api.get<Options>('/api/view-as/options').then(setOpts, (e) => setErr({ text: e instanceof Error ? e.message : t.failed, stepUp: false }));
  }, []);
  const go = async () => {
    if (!pick) return;
    setBusy(true);
    setErr(null);
    try {
      await startViewAs(p.api, pick.startsWith('r:') ? { role: pick.slice(2) } : { personId: pick.slice(2) });
    } catch (e) {
      setBusy(false);
      setErr({ text: e instanceof Error && e.message ? e.message : t.failed, stepUp: e instanceof ApiError && e.code === 'step_up_required' });
    }
  };
  return (
    <Dialog
      title={t.title}
      sub={t.sub}
      onClose={p.onClose}
      onSubmit={() => void go()}
      foot={
        <>
          <button type="button" class="btn btn-ghost" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={!pick || busy}>
            {t.start}
          </button>
        </>
      }
    >
      <div class="field">
        <label class="label" for="va-pick">
          {t.pick}
        </label>
        <select id="va-pick" class="input input--wide" value={pick} onChange={(e) => setPick((e.target as HTMLSelectElement).value)} aria-describedby="va-hint">
          <option value="">{t.choose}</option>
          {opts ? (
            <>
              <optgroup label={t.people}>
                {opts.people.map((x) => (
                  <option value={`p:${x.id}`} key={x.id}>{`${x.name} · ${x.roles.join(', ')}`}</option>
                ))}
              </optgroup>
              <optgroup label={t.roles}>
                {opts.roles.map((r) => (
                  <option value={`r:${r.key}`} key={r.key}>
                    {r.label}
                  </option>
                ))}
              </optgroup>
            </>
          ) : null}
        </select>
        <span class="hint" id="va-hint">
          {t.roleHint}
        </span>
      </div>
      {err ? (
        <div class="callout callout--warn" role="alert">
          <p>{err.text}</p>
          {err.stepUp ? (
            <button
              type="button"
              class="btn"
              onClick={() => {
                saveSession(null);
                location.assign('/');
              }}
            >
              {t.signIn}
            </button>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
